// What *kind* of game this is, for every zone that has to gate on it.
//
// A dozen surfaces ask the same handful of questions — is anybody watching, is
// there a seat, is a machine playing, is this a run — and each of them was
// answered by a prop threaded down from `GameShell`. They belong together
// because they change together: a mode is settled when the game opens and moves
// at most once a stage.
//
// **The seat is defined once, in the shell, and passed in here** (CLAUDE.md:
// `seatedAs` is the single definition of what you may move and which way the
// camera faces). This provider does not derive it — a second derivation is a
// second answer, and the two would disagree the first time a rule moved.
//
// Not in here: anything that moves per turn or per frame. A context is a
// re-render for every consumer, so the board, the selection and the camera
// preferences each stay where they are.

import { createContext, useContext, useMemo, type ReactNode } from "react";

import type { Level } from "src/engine/ai/difficulty";
import type { LevelSource } from "src/engine/campaign/levels";
import type { Side } from "src/engine/rules/board";

export interface ShellMode {
  /** Nobody is playing: both armies are driven and the board is a spectacle. */
  watching: boolean;
  /** Which starting position, and that a bot plays the other army. */
  scenario: string | undefined;
  /** The one side this client plays, or null when it plays both (hot-seat). */
  seatedAs: Side | null;
  /** The same, except while watching, where it is `NOBODY` — no seat at all. */
  controlling: Side | null;
  /** A scenario or a run: one person at the keyboard against machines. */
  solo: boolean;
  /** A cleared front asks the player which way the board grows (PLAN §19b). */
  branching: boolean;
  /** A machine plays the other armies — not "is one on the clock right now". */
  machinePlays: boolean;
  /** How well the opponent plays, as picked. */
  level: Level;
  /** The level actually in force, which a run climbs to (`levelForStage`). */
  playing: Level;
  onExit?(): void;
  onRestart?(): void;
  /** Open a level in place of the board — the dev panel's door, a run only. */
  onLoadLevel?(source: LevelSource): void;
  onPrivacy?(): void;
}

const ShellModeContext = createContext<ShellMode | null>(null);

/**
 * Memoised on the fields, so a consumer re-renders when the *mode* changes and
 * not when the board does. Every field is a primitive or an identity the shell
 * already holds stable, which is what makes the dependency list honest.
 */
export const ShellModeProvider = ({
  children,
  ...mode
}: ShellMode & { children: ReactNode }) => {
  const {
    watching,
    scenario,
    seatedAs,
    controlling,
    solo,
    branching,
    machinePlays,
    level,
    playing,
    onExit,
    onRestart,
    onLoadLevel,
    onPrivacy
  } = mode;
  const value = useMemo<ShellMode>(
    () => ({
      watching,
      scenario,
      seatedAs,
      controlling,
      solo,
      branching,
      machinePlays,
      level,
      playing,
      onExit,
      onRestart,
      onLoadLevel,
      onPrivacy
    }),
    [
      watching,
      scenario,
      seatedAs,
      controlling,
      solo,
      branching,
      machinePlays,
      level,
      playing,
      onExit,
      onRestart,
      onLoadLevel,
      onPrivacy
    ]
  );
  return <ShellModeContext.Provider value={value}>{children}</ShellModeContext.Provider>;
};

/**
 * What kind of game this is. Throws outside the provider: a zone that fell back
 * to a default would draw the *wrong game* — a watched board with a hotbar on
 * it, say — rather than fail where the mistake is.
 */
export function useShellMode(): ShellMode {
  const mode = useContext(ShellModeContext);
  if (!mode) throw new Error("useShellMode outside ShellModeProvider");
  return mode;
}
